import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { baseUrl } from 'utils/axios'
import {
  areDatesSameDay,
  formatParams,
  getParams,
  handleErrors,
  minutesToUTCDate,
  toStartOfUtcDay,
} from 'utils/helpers'
import {
  IPostAttendanceRequest,
  IGetAllAttendanceSingleResponse,
  IPostAttendanceRequestResponse,
  IPostMultipleAttendanceRequest,
  IGetAllActionableAttendanceReportsSingleResponse,
  IGetAllCompanyAttendanceReportsSingleResponse,
  IGetAllAttendancePoliciesSingleResponse,
  ICreateAttendancePolicyPostRequest,
  IGetSingleAttendancePolicy,
} from 'types/api/attendanceApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IAttendanceModal } from 'pages/Attendance/AttendanceModal'
import { v4 } from 'uuid'
import { IAttendancePolicyState, resolverTypeOptions } from 'pages/Attendance/Policies/AddPolicy'
import { ISelectValue } from 'constants/globalTypes'

interface IAttendanceSlice {
  attendance: IGetAllAttendanceSingleResponse[]
  totalCount: number
  totalPages: number
  isLoading: boolean
  noAttendances: boolean
  singlePolicy: IAttendancePolicyState
  actionableReports: {
    data: IGetAllActionableAttendanceReportsSingleResponse[]
    totalCount: number
    totalPages: number
  }
  companyReports: {
    data: IGetAllCompanyAttendanceReportsSingleResponse[]
    totalCount: number
    totalPages: number
  }

  policies: {
    data: IGetAllAttendancePoliciesSingleResponse[]
    totalCount: number
    totalPages: number
  }
}

const initialState: IAttendanceSlice = {
  attendance: [],
  totalCount: 0,
  totalPages: 0,
  isLoading: false,
  noAttendances: true,
  singlePolicy: null,
  actionableReports: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },

  companyReports: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },

  policies: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

// GET: /v1/attendance/management/users
export const getAttendances = createAsyncThunk(
  'attendance/getAttendances',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAttendanceSingleResponse>> =
        await baseUrl.get('attendance/management/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getPolicies = createAsyncThunk(
  'attendance/getPolicies',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAttendancePoliciesSingleResponse>> =
        await baseUrl.get('attendance/management/approval/policy', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getActionableReports = createAsyncThunk(
  'attendance/getActionableReports',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAttendanceSingleResponse>> =
        await baseUrl.get('attendance/management/report/actionable', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCompanyReports = createAsyncThunk(
  'attendance/getCompanyReports',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllCompanyAttendanceReportsSingleResponse>> =
        await baseUrl.get('attendance/management/report', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deletePolicy = createAsyncThunk(
  'attendance/deletePolicy',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.delete(
        'attendance/management/approval/policy/' + id,
      )
      await thunkAPI.dispatch(getPolicies(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const activatePolicy = createAsyncThunk(
  'attendance/activatePolicy',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.patch(`attendance/management/approval/policy/${id}/active/toggle`)

      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSinglePolicy = createAsyncThunk(
  'attendance/getSinglePolicy',
  async (id: any, thunkAPI) => {
    try {
      const { data }: AxiosResponse<IGetSingleAttendancePolicy> = await baseUrl.get(
        'attendance/management/approval/policy/' + id,
      )
      const obj: IAttendancePolicyState = {
        name: data.name,
        description: data.description,
        workflowSteps: data.workflowSteps.map((ws) => ({
          order: ws.order,
          name: ws.name,
          resolverType: resolverTypeOptions.find((t) => t.value == ws.resolverType),
          roles: ws.roles.map((r) => ({ label: r.role.name, value: r.role.id })),
          users: ws.users.map((u) => ({
            label: u.user.firstName + ' ' + u.user.lastName,
            value: u.user.id,
          })),
        })),
      }
      return obj
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addPolicy = createAsyncThunk(
  'attendance/addPolicy',
  async (state: IAttendancePolicyState, thunkAPI) => {
    try {
      const req: ICreateAttendancePolicyPostRequest = {
        name: state.name,
        description: state.description,
        workflowSteps: state.workflowSteps.map((ws) => ({
          order: ws.order,
          name: ws.name,
          resolverType: ws.resolverType.value,
          roleIds: ws.roles.map((r) => r.value),
          userIds: ws.users.map((u) => u.value),
        })),
      }
      const resp: AxiosResponse = await baseUrl.post('attendance/management/approval/policy', req)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editPolicy = createAsyncThunk(
  'attendance/editPolicy',
  async ({ state, id }: { state: IAttendancePolicyState; id: string }, thunkAPI) => {
    try {
      const req: ICreateAttendancePolicyPostRequest = {
        name: state.name,
        description: state.description,
        workflowSteps: state.workflowSteps.map((ws) => ({
          order: ws.order,
          name: ws.name,
          resolverType: ws.resolverType.value,
          roleIds: ws.roles.map((r) => r.value),
          userIds: ws.users.map((u) => u.value),
        })),
      }
      const resp: AxiosResponse = await baseUrl.patch(
        'attendance/management/approval/policy/' + id,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// POST: /v1/attendance/management/attendance
export const createAttendance = createAsyncThunk(
  'attendance/createAttendance',
  async (state: IAttendanceModal, thunkAPI) => {
    try {
      const req: IPostAttendanceRequest = {
        entryType: !!state.shiftId ? 1 : 2,
        workScheduleId: state.shiftId || null,
        userId: state.userId,
        type: state.attendanceType.value,
        startDate: state.checkIn
          ? minutesToUTCDate(state.checkIn, state.date).toISOString()
          : state.date.toISOString(),
        endDate: state?.checkOut
          ? minutesToUTCDate(state?.checkOut, state.date).toISOString()
          : null,
      }
      const resp: AxiosResponse<IPostAttendanceRequestResponse[]> = await baseUrl.post(
        'attendance/management/attendance',
        req,
      )
      return { userId: state.userId, data: { ...resp.data, startDate: req.startDate } }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createMultipleAttendance = createAsyncThunk(
  'attendance/createMultipleAttendance',
  async (req: IPostMultipleAttendanceRequest, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPostAttendanceRequestResponse[]> = await baseUrl.post(
        'attendance/management/attendance/multiple',
        req,
      )

      return {
        userIds: req.userIds,
        data: resp.data.map((d) => ({ ...d, startDate: req.startDate })),
      }
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createAttendanceReport = createAsyncThunk(
  'attendance/createAttendanceReport',
  async ({ dateFrom, dateTo }: { dateFrom: Date; dateTo: Date }, thunkAPI) => {
    try {
      await baseUrl.post('attendance/management/report', { dateFrom, dateTo })
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const resolveAttendanceReport = createAsyncThunk(
  'attendance/resolveAttendanceReport',
  async (
    {
      resolutionStatus,
      comment,
      id,
    }: { resolutionStatus: ISelectValue; comment: string; id: string },
    thunkAPI,
  ) => {
    try {
      await baseUrl.post(`attendance/management/report/${id}/workflow-step/resolve`, {
        resolutionStatus: resolutionStatus.value,
        comment,
      })
      await thunkAPI.dispatch(getActionableReports(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// DELETE: /v1/attendance/management/attendance/{id}
export const deleteAttendance = createAsyncThunk(
  'attendance/deleteAttendance',
  async ({ id, userId }: { id: number; userId: number }, thunkAPI) => {
    try {
      await baseUrl.delete(`attendance/management/attendance/${id}`)
      return { id, userId }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createSnapshot = createAsyncThunk(
  'attendance/createSnapshot',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`attendance/management/report/${id}/snapshot`)
      await thunkAPI.dispatch(getActionableReports(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

// POST: /v1/attendance/management/import
export const importAttendances = createAsyncThunk(
  'attendance/importAttendances',
  async ({ file, should }: { file: File; should: boolean }, thunkAPI) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('shouldImportNonWorkScheduleAttendance', String(should))
    try {
      const resp: AxiosResponse = await baseUrl.post('attendance/management/import', formData)
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    clearAttendances: (state) => {
      state.attendance = []
      state.totalCount = 0
      state.totalPages = 0
      state.isLoading = false
      state.noAttendances = true
    },
    clearPolicies: (state) => {
      state.policies.data = []
      state.policies.totalCount = 0
      state.policies.totalPages = 0
    },
  },
  extraReducers: {
    [getAttendances.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAttendances.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllAttendanceSingleResponse> },
    ) => {
      state.isLoading = false
      state.attendance = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.noAttendances = payload.data.length < 1
    },
    [getAttendances.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deletePolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [deletePolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Policy deleted successfully')
    },
    [deletePolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [activatePolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [activatePolicy.fulfilled.type]: (state, { payload }: { payload: number }) => {
      state.isLoading = false
      state.policies.data = state.policies.data.map((p) => {
        if (p.id == payload) {
          p.isActive = true
        }
        return p
      })
      toast.success('Policy activated successfully')
    },
    [activatePolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSinglePolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSinglePolicy.fulfilled.type]: (state, { payload }: { payload: IAttendancePolicyState }) => {
      state.isLoading = false
      state.singlePolicy = payload
    },
    [getSinglePolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },

    [getPolicies.pending.type]: (state) => {
      state.isLoading = true
    },
    [getPolicies.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllAttendancePoliciesSingleResponse> },
    ) => {
      state.isLoading = false
      state.policies.data = payload.data
    },
    [getPolicies.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getActionableReports.pending.type]: (state) => {
      state.isLoading = true
    },
    [getActionableReports.fulfilled.type]: (
      state,
      {
        payload,
      }: { payload: IPaginatedResponse<IGetAllActionableAttendanceReportsSingleResponse> },
    ) => {
      state.isLoading = false
      state.actionableReports.data = payload.data
    },
    [getActionableReports.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getCompanyReports.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCompanyReports.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IGetAllCompanyAttendanceReportsSingleResponse> },
    ) => {
      state.isLoading = false
      state.companyReports.data = payload.data
    },
    [getCompanyReports.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createAttendanceReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [createAttendanceReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Report created successfully')
    },
    [createAttendanceReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [addPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Policy added successfully')
    },
    [addPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [editPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Policy updated successfully')
    },
    [editPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [createAttendance.fulfilled.type]: (
      state,
      { payload }: { payload: { userId: number; data: IPostAttendanceRequestResponse } },
    ) => {
      state.isLoading = false
      state.attendance = state.attendance.map((a) => {
        if (a.id === payload.userId) {
          const inputExists = a.workScheduleInfos.find((wsi) =>
            areDatesSameDay(wsi.date, payload.data.startDate),
          )
          if (inputExists) {
            a.workScheduleInfos.map((wsi) => {
              if (areDatesSameDay(wsi.date, payload.data.startDate)) {
                wsi.workScheduleAttendanceInfo = payload.data
                return wsi
              }
              return wsi
            })
            return a
          } else {
            // ako ne postoji za odabrani dan
            a.workScheduleInfos.push({
              date: toStartOfUtcDay(payload.data.startDate).toISOString(),
              workSchedule: null,
              workScheduleAttendanceInfo: payload.data,
            })
            return a
          }
        }
        return a
      })

      toast.success('Attendance added')
    },
    [createAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false

      handleErrors(payload)
    },
    [createMultipleAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [createMultipleAttendance.fulfilled.type]: (
      state,
      {
        payload,
      }: {
        payload: { userIds: number[]; data: IPostAttendanceRequestResponse[] }
      },
    ) => {
      state.attendance = state.attendance.map((a) => {
        if (payload.userIds.includes(a.id)) {
          if (a.workScheduleInfos.length) {
            if (
              a.workScheduleInfos.some((wsi) =>
                areDatesSameDay(wsi.date, payload.data[0].startDate),
              )
            ) {
              a.workScheduleInfos = a.workScheduleInfos.map((wsi) => {
                if (areDatesSameDay(wsi.date, payload.data[0].startDate)) {
                  wsi.workScheduleAttendanceInfo = { ...payload.data[0], id: Number(v4()) }
                  return wsi
                }
                return wsi
              })
            } else {
              a.workScheduleInfos.push({
                date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
                workSchedule: null,
                workScheduleAttendanceInfo: payload.data[0],
              })
            }
          } else {
            a.workScheduleInfos.push({
              date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
              workSchedule: null,
              workScheduleAttendanceInfo: payload.data[0],
            })
          }
          return a
        }
        return a
      })

      // state.attendance = state.attendance.map((a) => {
      //   if (payload.userIds.includes(a.id)) {
      //     if (a.workScheduleInfos.length) {
      //       a.workScheduleInfos = a.workScheduleInfos.map((wsi) => {
      //         if (areDatesSameDay(wsi.date, payload.data[0].startDate)) {
      //           wsi.workScheduleAttendanceInfo = payload.data[0]
      //           return wsi
      //         } else {
      //           return wsi
      //         }
      //       })
      //     } else {
      //       a.workScheduleInfos.push({
      //         date: toStartOfUtcDay(payload.data[0].startDate).toISOString(),
      //         workSchedule: null,
      //         workScheduleAttendanceInfo: payload.data[0],
      //       })
      //     }
      //     return a
      //   } else {
      //     return a
      //   }
      // })
      state.isLoading = false
      toast.success('Attendance added')
    },
    [createMultipleAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false

      handleErrors(payload)
    },
    [deleteAttendance.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteAttendance.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.attendance = state.attendance.map((a) => {
        if (a.id !== payload.userId) {
          return a
        } else {
          return {
            ...a,
            workScheduleInfos: a.workScheduleInfos.filter(
              (s) => s.workScheduleAttendanceInfo?.id !== payload.id,
            ),
          }
        }
      })
      toast.success('Attendance deleted')
    },
    [deleteAttendance.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [importAttendances.pending.type]: (state) => {
      state.isLoading = true
    },
    [importAttendances.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [importAttendances.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [resolveAttendanceReport.pending.type]: (state) => {
      state.isLoading = true
    },
    [resolveAttendanceReport.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Report successfully resolved')
    },
    [resolveAttendanceReport.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createSnapshot.pending.type]: (state) => {
      state.isLoading = true
    },
    [createSnapshot.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Snapshot successfully created')
    },
    [createSnapshot.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearAttendances, clearPolicies } = attendanceSlice.actions
export default attendanceSlice.reducer
